import { createContext, useContext, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { EffectsContextType, EffectConfig } from '@/types/game';
import { EffectsManager } from '@/game/effects';

const EffectsContext = createContext<EffectsContextType | undefined>(undefined);

interface EffectsProviderProps {
  children: ReactNode;
}

export function EffectsProvider({ children }: EffectsProviderProps) {
  const effectsEnabledRef = useRef(localStorage.getItem('effects') !== 'false');
  const activeEffectsRef = useRef<Map<string, HTMLElement>>(new Map());
  const effectsManagerRef = useRef<EffectsManager | null>(null);
  const graphicsQualityRef = useRef(localStorage.getItem('graphics') || 'high');
  
  // Listen for storage changes to update settings
  useEffect(() => {
    const handleStorageChange = () => {
      effectsEnabledRef.current = localStorage.getItem('effects') !== 'false';
      graphicsQualityRef.current = localStorage.getItem('graphics') || 'high';
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Initialize EffectsManager on mount
  useEffect(() => {
    if (!effectsManagerRef.current) {
      effectsManagerRef.current = new EffectsManager();
    }
    return () => {
      effectsManagerRef.current = null;
    };
  }, []);

  const triggerEffect = useCallback((config: EffectConfig) => {
    if (!effectsEnabledRef.current) return;
    
    // Performance optimization based on graphics quality
    const quality = graphicsQualityRef.current;
    if (quality === 'low') {
      // Skip non-essential effects in low quality mode
      if (config.type === 'particle' || config.type === 'sparkle') {
        return;
      }
      // Reduce intensity for other effects
      if (config.intensity) {
        config.intensity = Math.ceil(config.intensity / 2);
      }
    } else if (quality === 'medium') {
      // Reduce intensity slightly for medium quality
      if (config.intensity && config.intensity > 10) {
        config.intensity = Math.ceil(config.intensity * 0.75);
      }
    }

    // Use EffectsManager for better performance and features
    if (effectsManagerRef.current) {
      const manager = effectsManagerRef.current;
      const position = config.position ? { x: config.position[0], y: config.position[1] } : { x: 50, y: 50 };
      
      switch (config.type) {
        case 'confetti':
          manager.createConfetti(position.x, position.y, config.intensity || 20);
          break;
        case 'explosion':
          manager.createExplosion(position.x, position.y, config.color);
          break;
        case 'glow':
          const character = document.querySelector('.game__character') as HTMLElement;
          if (character) {
            manager.addGlow(character, config.color, config.intensity || 1);
          }
          break;
        case 'particle':
          // Create multiple explosion particles for particle effect
          for (let i = 0; i < (config.intensity || 10); i++) {
            manager.createExplosion(
              position.x + (Math.random() - 0.5) * 20,
              position.y + (Math.random() - 0.5) * 20,
              config.color || '#4ecdc4'
            );
          }
          break;
        case 'shake':
          manager.screenShake(config.duration || 500, config.intensity || 5);
          break;
        case 'sparkle':
          // Use sparkle effect
          manager.createSparkle(position.x, position.y, config.intensity || 10, config.color);
          break;
        case 'coinCollect':
          manager.createCoinEffect(position.x, position.y, config.intensity || 10);
          break;
      }
      return;
    }

    // Fallback to existing implementation
    const effectId = `effect-${Date.now()}-${Math.random()}`;
    
    switch (config.type) {
      case 'confetti':
        createConfettiEffect(effectId, config);
        break;
      case 'explosion':
        createExplosionEffect(effectId, config);
        break;
      case 'glow':
        createGlowEffect(effectId, config);
        break;
      case 'particle':
        createParticleEffect(effectId, config);
        break;
      case 'shake':
        createShakeEffect(effectId, config);
        break;
    }
  }, []);

  const createConfettiEffect = (id: string, config: EffectConfig) => {
    const container = document.createElement('div');
    container.className = 'confetti-container';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 9999;
    `;

    // Create confetti particles
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.className = 'confetti-particle';
      particle.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background: hsl(${Math.random() * 360}, 70%, 60%);
        left: ${config.position?.[0] || Math.random() * 100}%;
        top: ${config.position?.[1] || Math.random() * 100}%;
        transform: rotate(${Math.random() * 360}deg);
        animation: confetti-fall ${config.duration || 2000}ms ease-out forwards;
      `;
      container.appendChild(particle);
    }

    document.body.appendChild(container);
    activeEffectsRef.current.set(id, container);

    setTimeout(() => {
      container.remove();
      activeEffectsRef.current.delete(id);
    }, config.duration || 2000);
  };

  const createExplosionEffect = (id: string, config: EffectConfig) => {
    const container = document.createElement('div');
    container.className = 'explosion-container';
    container.style.cssText = `
      position: fixed;
      left: ${config.position?.[0] || 50}%;
      top: ${config.position?.[1] || 50}%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 9998;
    `;

    const explosion = document.createElement('div');
    explosion.className = 'explosion-effect';
    explosion.style.cssText = `
      width: 100px;
      height: 100px;
      background: radial-gradient(circle, ${config.color || '#ff6b35'}, transparent);
      border-radius: 50%;
      animation: explosion-expand ${config.duration || 600}ms ease-out forwards;
    `;

    container.appendChild(explosion);
    document.body.appendChild(container);
    activeEffectsRef.current.set(id, container);

    setTimeout(() => {
      container.remove();
      activeEffectsRef.current.delete(id);
    }, config.duration || 600);
  };

  const createGlowEffect = (_id: string, config: EffectConfig) => {
    const targetElement = document.elementFromPoint(
      config.position?.[0] || 0, 
      config.position?.[1] || 0
    ) as HTMLElement;
    
    if (!targetElement) return;

    const originalBoxShadow = targetElement.style.boxShadow;
    targetElement.style.boxShadow = `0 0 20px ${config.color || '#4ecdc4'}`;
    targetElement.style.transition = 'box-shadow 0.3s ease';

    setTimeout(() => {
      targetElement.style.boxShadow = originalBoxShadow;
    }, config.duration || 1000);
  };

  const createParticleEffect = (id: string, config: EffectConfig) => {
    const container = document.createElement('div');
    container.className = 'particle-container';
    container.style.cssText = `
      position: fixed;
      left: ${config.position?.[0] || 50}%;
      top: ${config.position?.[1] || 50}%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 9997;
    `;

    const particleCount = config.intensity || 20;
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 4px;
        height: 4px;
        background: ${config.color || '#4ecdc4'};
        border-radius: 50%;
        animation: particle-burst ${config.duration || 1000}ms ease-out forwards;
        animation-delay: ${i * 50}ms;
      `;
      container.appendChild(particle);
    }

    document.body.appendChild(container);
    activeEffectsRef.current.set(id, container);

    setTimeout(() => {
      container.remove();
      activeEffectsRef.current.delete(id);
    }, (config.duration || 1000) + (particleCount * 50));
  };

  const createShakeEffect = (id: string, config: EffectConfig) => {
    const gameContainer = document.getElementById('game-container') || document.body;
    const originalTransform = gameContainer.style.transform;
    
    let shakeCount = 0;
    const maxShakes = config.intensity || 10;
    const shakeDuration = config.duration || 500;
    const shakeInterval = shakeDuration / maxShakes;

    const shakeTimer = setInterval(() => {
      if (shakeCount >= maxShakes) {
        clearInterval(shakeTimer);
        gameContainer.style.transform = originalTransform;
        activeEffectsRef.current.delete(id);
        return;
      }

      const offsetX = (Math.random() - 0.5) * 10;
      const offsetY = (Math.random() - 0.5) * 10;
      gameContainer.style.transform = `${originalTransform} translate(${offsetX}px, ${offsetY}px)`;
      
      shakeCount++;
    }, shakeInterval);

    activeEffectsRef.current.set(id, gameContainer);
  };

  const clearEffects = () => {
    activeEffectsRef.current.forEach((element) => {
      element.remove();
    });
    activeEffectsRef.current.clear();
  };

  const setEffectsEnabled = (enabled: boolean) => {
    effectsEnabledRef.current = enabled;
    localStorage.setItem('effects', enabled.toString());
    
    if (!enabled) {
      clearEffects();
    }
  };

  // Character-specific animation methods
  const animateCharacterMove = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    
    const character = document.querySelector('.game__character') as HTMLElement;
    const gameBoard = document.querySelector('.game') as HTMLElement;
    
    if (character && gameBoard) {
      effectsManagerRef.current.animateCharacterMove(character);
      // Add direction-specific class for CSS animations
      character.classList.remove('moving-up', 'moving-down', 'moving-left', 'moving-right', 'idle');
      character.classList.add(`moving-${direction}`);
      
      // Clear idle timeout
      if ((window as any).idleTimeout) {
        clearTimeout((window as any).idleTimeout);
      }
      
      // Create energy trail effect
      const trail = document.createElement('div');
      trail.className = `player-trail trail-${direction}`;
      
      // Get computed styles to position trail correctly
      const computedStyle = window.getComputedStyle(character);
      const currentTop = computedStyle.getPropertyValue('--positionTop');
      const currentLeft = computedStyle.getPropertyValue('--positionLeft');
      
      trail.style.top = `calc(${currentTop} * var(--cell))`;
      trail.style.left = `calc(${currentLeft} * var(--cell))`;
      
      gameBoard.appendChild(trail);
      
      // Remove trail after animation
      setTimeout(() => {
        trail.remove();
      }, 600);
      
      // Set idle animation after 1 second
      (window as any).idleTimeout = setTimeout(() => {
        character.classList.add('idle');
      }, 1000);
    }
  }, []);

  const animateCharacterImpact = useCallback(() => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    
    const character = document.querySelector('.game__character') as HTMLElement;
    if (character) {
      effectsManagerRef.current.animateCharacterImpact(character);
      // Add screen shake for impact
      effectsManagerRef.current.screenShake(300, 3);
    }
  }, []);

  const animateCharacterVictory = useCallback(() => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    
    const character = document.querySelector('.game__character') as HTMLElement;
    if (character) {
      effectsManagerRef.current.animateCharacterVictory(character);
      // Add confetti for victory
      effectsManagerRef.current.createConfetti(50, 50, 30);
    }
  }, []);

  const animateCharacterEntrance = useCallback((element: HTMLElement) => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    
    // Add entrance animation class
    element.classList.add('character-entering');
    
    // Create entrance effect
    effectsManagerRef.current.createExplosion(50, 50, '#4ecdc4');
    
    // Remove animation class after completion
    setTimeout(() => {
      element.classList.remove('character-entering');
    }, 600);
  }, []);

  const createScreenFlash = useCallback((color?: string, opacity?: number) => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    effectsManagerRef.current.createScreenFlash(color, opacity);
  }, []);

  const animateLevelTransition = useCallback((callback?: () => void) => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    effectsManagerRef.current.animateLevelTransition(callback);
  }, []);

  const showAchievement = useCallback((text: string, icon?: string) => {
    if (!effectsManagerRef.current || !effectsEnabledRef.current) return;
    effectsManagerRef.current.showAchievement(text, icon);
  }, []);

  const contextValue: EffectsContextType = {
    triggerEffect,
    clearEffects,
    setEffectsEnabled,
    animateCharacterMove,
    animateCharacterImpact,
    animateCharacterVictory,
    animateCharacterEntrance,
    createScreenFlash,
    animateLevelTransition,
    showAchievement,
  };

  return (
    <EffectsContext.Provider value={contextValue}>
      {children}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes explosion-expand {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }
        
        @keyframes particle-burst {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(calc(var(--random-x, 0) * 100px), calc(var(--random-y, 0) * 100px)) scale(0);
            opacity: 0;
          }
        }
        
        @keyframes dust-puff {
          0% {
            transform: translateX(-50%) scale(0.5);
            opacity: 0.8;
          }
          100% {
            transform: translateX(-50%) scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </EffectsContext.Provider>
  );
}

export function useEffects() {
  const context = useContext(EffectsContext);
  if (context === undefined) {
    throw new Error('useEffects must be used within an EffectsProvider');
  }
  return context;
}